import { useMemo, useEffect } from "react";
import { Popover, message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { CommonButton } from "@components/CommonButton";
import { IconCredit, IconFileEmpty } from "@icons/index";
import {
  setDescParams,
  selectVideoCreatingParams,
  selectStepStatus,
  setStepStatus,
  fetchReferenceScriptData,
  setVideoGenerating,
  selectVideoGenerating,
} from "../videoCreatingSlice";
import { fetchAiCredits } from "@store/globalSlice";
import MemberPopoverContent from "@components/MemberPopoverContent";
import type { BuildVideoReferenceScriptRequest, VideoCreationRequest } from "@apis/videoCreatingService";
import { createVideoCreation } from "@apis/http/api";
import { shouldShowNotificationGuide } from "@utils/notification";
import { notifyGeneratedVideoAssetsChanged } from "@pages/library/hooks/useGeneratedVideoAssets";
import { notificationModalAtom } from "@src/jotai/notification";
import { useSetAtom, useAtomValue } from "jotai";
import "./index.scss";
import TextArea from "antd/es/input/TextArea";
import { useNavigate } from "react-router-dom";
import { VideoParamsSelect } from "../VideoParamsSelect";
import { useConsumeCredit } from "../hooks";
import report from "@report";
import LoadingDark from "./loading_dark.gif";
import LoadingLight from "./loading_light.gif";
import { themeActionAtom } from "@src/jotai/theme";
import { isSeeDanceModel, truncateUnicodeText } from "@utils/util";

export const VideoGeneratingLoading = ({ countDown, className }: { countDown: number; className?: string }) => {
  const messages = [
    { delay: 0, key: "Connecting to Kalodata..." },
    { delay: 6, key: "Analyzing viral videos from Kalodata..." },
    { delay: 21, key: "Generating your script..." },
  ];
  const isDark = useAtomValue(themeActionAtom) === "dark";

  const loadingText = useMemo(() => {
    const elapsed = 180 - countDown;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (elapsed >= messages[i].delay) return t(messages[i].key);
    }
    return t(messages[0].key);
  }, [countDown, messages]);

  return (
    <div className={`flex-1 flex flex-col items-center justify-center ${className || ""}`}>
      {isDark ? (
        <img src={LoadingDark} alt="loading" className="w-[100px] h-[100px] mix-blend-screen" />
      ) : (
        <img src={LoadingLight} alt="loading" className="w-[100px] h-[100px]" />
      )}
      <span className="text-kds-fg text-[16px] font-semibold mt-[16px]">{loadingText}</span>
      <span className="text-kds-fg-subtle text-[13px] font-normal mt-[4px]">
        <Trans
          i18nKey="video_script_countdown"
          defaults="Good things take time (about {{countDown}}s)"
          values={{ countDown }}
        />{" "}
        ⏳
      </span>
    </div>
  );
};

const AutoDescription = ({ onGenerateSuccess }: { onGenerateSuccess?: () => void }) => {
  const dispatch = useAppDispatch();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const params = useAppSelector(selectVideoCreatingParams);
  const { basicInfo, productInfo, descParams, productId, country } = params;
  const { images, productTitle } = basicInfo;
  const { sellingCountry, language } = productInfo;
  const {
    aspectRatio,
    duration,
    quantity,
    resolution,
    originScript,
    videoScript,
    videoScriptStatus,
    countDown,
    internalModelId,
  } = descParams;
  const isSeeDance = isSeeDanceModel(internalModelId);
  const videoScriptLimit = isSeeDance ? 3000 : 4000;
  const stepStatus = useAppSelector(selectStepStatus);
  const navigate = useNavigate();

  const loading = useAppSelector(selectVideoGenerating);
  const totalCost = useConsumeCredit();
  const setNotificationModal = useSetAtom(notificationModalAtom);

  const loadReferenceScript = async () => {
    const { productCategory } = basicInfo;
    const imageInfos: BuildVideoReferenceScriptRequest["imageInfos"] = images.map<
      BuildVideoReferenceScriptRequest["imageInfos"][number]
    >((img) => {
      return {
        assetId: img.assetId || 0,
        imageUrl: img.url,
        description: img.description || "",
      } satisfies BuildVideoReferenceScriptRequest["imageInfos"][number];
    });

    if (imageInfos.length === 0 || !productTitle) {
      message.error("Missing required data: imageInfos or productTitle");
      return;
    }
    if (!internalModelId) {
      message.error(t("Please select a model"));
      return;
    }

    const categoryNames = productCategory
      ? productCategory
          .split(">")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const sellingPointsData = productInfo.sellingPoints?.data || [];
    const sellingPoints = sellingPointsData
      .filter((sp) => sp.checked)
      .map((sp) => sp.keyWord)
      .filter((s): s is string => Boolean(s));

    const requestData: BuildVideoReferenceScriptRequest = {
      imageInfos,
      internalModelId,
      productId: productId === "custom" ? "" : productId,
      productTitle,
      categoryNames: categoryNames.length > 0 ? categoryNames : [],
      sellingPoints: sellingPoints.length > 0 ? sellingPoints : undefined,
      sellingCountry: sellingCountry || "United States",
      country,
      language: language || "en",
      duration,
      creationDescription: productInfo.creationDescription?.trim() || undefined,
    };

    dispatch(fetchReferenceScriptData(requestData) as any);
  };

  // 进入第三步时加载参考脚本
  useEffect(() => {
    if (!stepStatus.descParamsInit) {
      loadReferenceScript();
      dispatch(setStepStatus({ descParamsInit: true } as any));
    }
  }, []);

  // 上报视频提示词生成结果
  useEffect(() => {
    if (videoScriptStatus === "succeeded") {
      report.event("generate_video", { kalo_type: "generate_video_prompt_success" });
    } else if (videoScriptStatus === "failed") {
      report.event("generate_video", { kalo_type: "generate_video_prompt_failed" });
    }
  }, [videoScriptStatus]);

  // 检查是否可以生成
  const canGenerate = useMemo(() => {
    // 积分不足
    if (totalRemain < totalCost) return false;
    // 没有脚本内容
    if (!videoScript) return false;
    return true;
  }, [totalRemain, totalCost, videoScript]);

  // 执行视频生成
  const performGenerate = async () => {
    if (!canGenerate || loading) return;

    report.click({ kalo_type: "generate_video" });

    try {
      if (!internalModelId) {
        message.error(t("Please select a model"));
        return;
      }

      dispatch(setVideoGenerating(true));
      // 刷新积分
      const res: any = await dispatch(fetchAiCredits());
      const currentRemain = res.payload?.totalRemain || 0;

      if (currentRemain < totalCost) {
        message.error(t("Insufficient credits"));
        return;
      }

      // 准备图片信息
      const imageInfos = images
        .filter((img) => img.url)
        .map((img) => ({
          assetId: img.assetId || 0,
          imageUrl: img.url,
          description: img.description || undefined,
          needsFaceMasking: img.needsFaceMasking ?? false,
        }));

      if (imageInfos.length === 0) {
        message.error(t("Please upload at least one image"));
        return;
      }

      const categoryNames = productInfo.category
        ? productInfo.category
            .split(">")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const sellingPointsData = productInfo.sellingPoints?.data || [];
      const sellingPoints = sellingPointsData
        .filter((sp) => sp.checked)
        .map((sp) => sp.keyWord)
        .filter((s): s is string => Boolean(s));

      // 构造请求参数
      const requestData: VideoCreationRequest = {
        imageInfos,
        aspectRatio,
        duration,
        quantity,
        resolution,
        productTitle,
        originScript,
        userModifiedScript: truncateUnicodeText(videoScript, videoScriptLimit),
        categoryNames: categoryNames.length > 0 ? categoryNames : [],
        sellingPoints: sellingPoints.length > 0 ? (sellingPoints as string[]) : undefined,
        sellingCountry: sellingCountry || "United States",
        language: language || "en",
        creationDescription: productInfo.creationDescription?.trim() || undefined,
        // 商品id和国家
        productId: productId === "custom" ? "" : productId,
        country,
        internalModelId,
      };
      // 调用视频生成 API
      const createRes: any = await createVideoCreation(requestData as any);
      report.event("generate_video", { kalo_type: "video_task_submitted", kalo_value: createRes?.jobId || "" });
      dispatch(fetchAiCredits());
      message.success(t("Video generation started"));
      notifyGeneratedVideoAssetsChanged();
      onGenerateSuccess?.();
      navigate("/library?type=video");
    } catch (error: any) {
      message.error(error?.message || t("Failed to generate video"));
    } finally {
      dispatch(setVideoGenerating(false));
    }
  };

  // 处理生成按钮点击
  const handleGenerate = () => {
    if (!canGenerate || loading) return;

    // 检查通知权限，弹窗关闭后继续生成
    if (shouldShowNotificationGuide()) {
      setNotificationModal({ open: true, onComplete: performGenerate });
      return;
    }

    performGenerate();
  };

  // 渲染生成按钮
  const renderGenerateBtn = () => {
    const scriptLoading = videoScriptStatus === "loading";
    const btnDisabled = !canGenerate || loading || scriptLoading;

    const button = (
      <CommonButton
        size="large"
        type="primary"
        className="min-w-[96px] new-btn"
        disabled={btnDisabled}
        loading={loading || scriptLoading}
        onClick={handleGenerate}
      >
        <Trans>Generate</Trans>
        <div className="flex items-center gap-[2px] ml-[8px] px-[4px] min-w-[45px] h-[22px] leading-[22px] credit-tag rounded-full">
          <IconCredit width={16} height={16} />
          <span className="font-semibold text-[14px]">{totalCost}</span>
        </div>
      </CommonButton>
    );

    // 积分不足提示
    if (totalRemain < totalCost) {
      return (
        <Popover content={<MemberPopoverContent remainCredit={totalRemain} action="Credits" />} trigger="hover">
          {button}
        </Popover>
      );
    }

    return button;
  };

  return (
    <div className="AutoDescription flex flex-col flex-1 bg-kds-bg relative">
      <div className="flex-1 overflow-y-auto p-[24px] flex flex-col border-x border-kds-border">
        <div className="flex items-center gap-1 mb-2">
          <span className="text-kds-fg font-bold text-[16px]">
            <Trans>Video Script</Trans>
          </span>
        </div>
        {videoScriptStatus === "loading" && <VideoGeneratingLoading countDown={countDown} />}
        {videoScriptStatus === "failed" && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <IconFileEmpty className="w-[100px] h-[100px]" />
            <div className="text-kds-fg text-[16px] font-medium mt-[8px]">
              <Trans>Script generation failed</Trans>
            </div>
            <div className="text-kds-fg-subtle text-[13px] font-normal mt-[4px]">
              <Trans>Something went wrong. Please try again.</Trans>
            </div>
            <div className="flex gap-[16px] mt-[16px]">
              <CommonButton type="primary" onClick={loadReferenceScript}>
                <Trans>Retry</Trans>
              </CommonButton>
              {/* <CommonButton onClick={() => dispatch(setDescParams({ videoScriptStatus: "succeeded" }))}>
                <Trans>Manually add</Trans>
              </CommonButton> */}
            </div>
          </div>
        )}
        {videoScriptStatus === "succeeded" && (
          <TextArea
            showCount
            maxLength={videoScriptLimit}
            disabled={loading}
            value={videoScript}
            onChange={(e) => dispatch(setDescParams({ videoScript: e.target.value }))}
            placeholder={t("Enter your video script here...")}
            className="script-textarea flex-1 p-0!"
            style={{ resize: "none" }}
          />
        )}
      </div>

      {/* Footer - 参数选择区 */}
      <div className="bg-kds-bg border-t border-kds-border p-[16px] flex items-center justify-between gap-[8px]">
        <VideoParamsSelect disabledDuration showResolution disabled={loading || videoScriptStatus === "loading"} />

        <div className="flex-1"></div>

        {renderGenerateBtn()}
      </div>
    </div>
  );
};

export default AutoDescription;
